import { OwnerTypeEnum, SignerType } from '~/enums';
import {
  getMarketHasAddress,
  getMarketOnlyAllowsBoardMemberAsGuarantor,
} from '~/constants/markets';
import moment from 'moment';
import { personalNumbersAreTheSame } from './utils';
import { parseMarket } from './market.helper';
import {
  formatBankAccountNumber,
  formatPersonalNumber,
} from './formatters.helper';
import { ICardOfferApplicant, ICardOfferState } from '~/interfaces/cardOffer';
import {
  CompanyTypeCategory,
  IKlarnaAccount,
  IAddressFields,
  IAMLData,
  IBeneficialOwner,
  IPerson,
  ISigner,
  IOpenBankingAccount,
} from '~/interfaces/shared';
import {
  ILoanApplicationForm,
  ILoanApplicationGuarantor,
} from '~/interfaces/loanApplication';
import {
  ILoanOfferApplicant,
  ILoanOfferForm,
  ILoanOfferState,
} from '~/interfaces/loanOffer';
import { AnyAction } from '@reduxjs/toolkit';
import { ApiStatus } from '~/store/types/sharedTypes';
import { TLoanOfferSliceActions } from '~/store/slices/loanOffer.slice';
import { TCardOfferSliceActions } from '~/store/slices/cardOffer.slice';
import { CountryCode } from '@qred/shared-enums';
import { AppDispatch } from '~/store';

export const buildStreetValue = ({
  streetName,
  houseNumber,
  houseNumberAddition,
}: IAddressFields) => {
  if (
    houseNumberAddition === 'NoAdditionalHouseNumber' ||
    !houseNumberAddition
  ) {
    return `${streetName} ${houseNumber}`.trim();
  }
  return `${streetName} ${houseNumber} ${houseNumberAddition}`.trim();
};

export const transformGuarantorAddress = ({
  streetName,
  houseNumber,
  houseNumberAddition,
  zipCode,
  city,
  market,
}: IAddressFields & { market?: CountryCode }) => {
  if (market === CountryCode.DE) {
    return `${streetName || ''} ${houseNumber || ''}${
      houseNumberAddition || ''
    }, ${zipCode || ''}, ${city || ''}`.trim();
  }
  if (
    houseNumberAddition === 'NoAdditionalHouseNumber' ||
    !houseNumberAddition
  ) {
    return `${streetName} ${houseNumber} ${zipCode} ${city}`.trim();
  }
  return `${streetName} ${houseNumber} ${houseNumberAddition} ${zipCode} ${city}`.trim();
};

/**
 *
 * @param addressResponseHouseNumberAdditions
 * @returns empty array if only item is empty string
 */
export const houseNumberAdditionsDropdownValuesHelper = (
  addressResponseHouseNumberAdditions: string[]
) => {
  if (!addressResponseHouseNumberAdditions.filter((x) => x).length) {
    return [];
  }

  const addressResponseDropdownValuesArray = [
    'Select',
    ...addressResponseHouseNumberAdditions,
  ].map((x) => (x === '' ? 'NoAdditionalHouseNumber' : x));

  return addressResponseDropdownValuesArray;
};

/**
 *
 * @param addressResponseHouseNumberAdditions
 * @returns empty array if only item is empty string
 */
export const houseNumberAdditionsDropdownValuesHelperV2 = (
  addressResponseHouseNumberAdditions: string[]
) => {
  if (
    !addressResponseHouseNumberAdditions.filter(
      (houseNumberAddition) => houseNumberAddition
    ).length
  ) {
    return [];
  }

  const addressResponseDropdownValuesArray = addressResponseHouseNumberAdditions.map(
    (houseNumberAddition) =>
      houseNumberAddition === ''
        ? 'NoAdditionalHouseNumber'
        : houseNumberAddition
  );

  return addressResponseDropdownValuesArray;
};

/**
 * Compares names and returns true if they match
 * Function tries to match even if middle names are missing.
 * E.g. "John Doe" and "John Doe Smith" would match.
 * They do not have to be in the same order, unless both names are the same length.
 * E.g. "John Doe" and "Doe John" would not match match.
 * @param name1
 * @param name2
 * @returns boolean
 */
export const compareNames = (
  name1: string | null | undefined,
  name2: string | null | undefined
): boolean => {
  const names1: string[] = (name1 ?? '').toLowerCase().trim().split(' ');
  const names2: string[] = (name2 ?? '').toLowerCase().trim().split(' ');

  if (names1.length === names2.length) {
    return names1.join(' ') === names2.join(' ');
  }

  const [moreNames, lessNames]: [string[], string[]] =
    names1.length > names2.length ? [names1, names2] : [names2, names1];

  return lessNames.every((part: string) => moreNames.includes(part));
};

export const personsAreTheSame = (person1: IPerson, person2: IPerson) => {
  const {
    personalNumber: personalNumber1,
    dateOfBirth: dateOfBirth1,
    yearOfBirth: yearOfBirth1,
  } = person1;
  const {
    personalNumber: personalNumber2,
    dateOfBirth: dateOfBirth2,
    yearOfBirth: yearOfBirth2,
  } = person2;
  const name1 =
    person1.fullName ??
    person1.name ??
    `${person1.firstName || ''} ${person1.lastName || ''}`.trim();
  const name2 =
    person2.fullName ??
    person2.name ??
    `${person2.firstName || ''} ${person2.lastName || ''}`.trim();

  if (personalNumber1 && personalNumber2) {
    return personalNumbersAreTheSame(personalNumber1, personalNumber2);
  }

  const namesMatch = compareNames(name1, name2);

  if (dateOfBirth1 && dateOfBirth2 && name1 && name2) {
    const date1 = moment(dateOfBirth1).format('YYYY-MM-DD');
    const date2 = moment(dateOfBirth2).format('YYYY-MM-DD');

    return date1 === date2 && namesMatch;
  }

  if (yearOfBirth1 && yearOfBirth2 && name1 && name2) {
    return yearOfBirth1 === yearOfBirth2 && namesMatch;
  }

  if (name1 && name2) {
    return namesMatch;
  }

  return false;
};

export const personAlreadyExistsInList = (
  personList: IPerson[],
  person: IPerson
) => personList.some((listPerson) => personsAreTheSame(listPerson, person));

/**
 *
 * @param form
 * @returns true if guarantor is manuallyAdded, else false
 */
export const hasManuallyAddedAGuarantor = (form: ILoanApplicationForm) =>
  form.guarantors.some(
    (guarantor: ILoanApplicationGuarantor) => guarantor.manuallyAdded
  );

export const getGuarantorContentByMarket = (
  form: ILoanApplicationForm,
  market: CountryCode
) => {
  const hasGuarantors = form.guarantors.length > 0;

  const marketOnlyAllowsBoardMemberAsGuarantor = getMarketOnlyAllowsBoardMemberAsGuarantor(
    market
  );

  let translationKey = '';
  if (hasGuarantors && marketOnlyAllowsBoardMemberAsGuarantor) {
    translationKey = 'AddAdditionalBoardMembersTextBoardMembersExist';
  } else if (!hasGuarantors && marketOnlyAllowsBoardMemberAsGuarantor) {
    translationKey = 'AddAdditionalBoardMembersTextBoardMembersNotExist';
  } else {
    translationKey = 'AddAdditionalGuarantorsText';
  }
  return translationKey;
};

export const getGuarantorText = (guarantor?: ILoanApplicationGuarantor) => {
  let translationKey = '';
  if (!guarantor) {
    translationKey = 'AddNewGuarantor';
  } else if (guarantor.manuallyAdded) {
    translationKey = 'EditGuarantor';
  } else if (guarantor.email && guarantor.phone) {
    translationKey = 'EditContactInformation';
  } else {
    translationKey = 'AddContactInformation';
  }
  return translationKey;
};

export const isBeneficialOwner = (ownership: string) => Number(ownership) > 25;

export const getOwnerType = (
  applicantOwnershipPercentage: string,
  additionalOwners: IBeneficialOwner[]
) => {
  // Applicant owns more than 75% (76% or more)
  if (Number(applicantOwnershipPercentage) > 75) {
    return OwnerTypeEnum.MoreThan75;
  }

  const allOwnerPercentages = [
    applicantOwnershipPercentage,
    ...additionalOwners.map((owner) => owner.percentage),
  ];

  // At least 1 owner owns more than 25%
  if (
    allOwnerPercentages.some((ownerPercentage) =>
      isBeneficialOwner(ownerPercentage)
    )
  ) {
    return OwnerTypeEnum.MorePersonsOwn25;
  }

  return OwnerTypeEnum.NonOneOwn25;
};

const setAMLData = (
  formData: URLSearchParams,
  amlData: IAMLData & {
    address?: IAddressFields | null;
  },
  prefix: string,
  suffix: string
) => {
  const market = parseMarket(window.__QRED_MARKET__);
  amlData.countryOfResidence &&
    formData.set(
      `${prefix}residence_country_code${suffix}`,
      amlData.countryOfResidence
    );
  amlData.countryOfTaxResidence &&
    formData.set(
      `${prefix}tax_residence_country_code${suffix}`,
      amlData.countryOfTaxResidence
    );
  amlData.isPEP !== undefined &&
    formData.set(`${prefix}is_pep${suffix}`, amlData.isPEP ? '1' : '0');

  if (market && getMarketHasAddress(market) && amlData.address) {
    formData.set(`${prefix}street${suffix}`, buildStreetValue(amlData.address));
    amlData.address.zipCode &&
      formData.set(`${prefix}zip_code${suffix}`, amlData.address.zipCode);
    amlData.address.city &&
      formData.set(`${prefix}city${suffix}`, amlData.address.city);
  }
};

const setOwner = (
  owner: Omit<IBeneficialOwner, 'id'>,
  formData: URLSearchParams,
  id: string
) => {
  const market = parseMarket(window.__QRED_MARKET__);

  const nonOneOwn25 = formData.get('owner_type') === OwnerTypeEnum.NonOneOwn25;

  formData.set(`owner_block_${id}`, id);
  formData.set(`owner_block_name_${id}`, owner.fullName);
  owner.isAlternativeBeneficialOwner &&
    formData.set(`owner_block_is_alternative_${id}`, '1');
  owner.personalNumber &&
    market &&
    formData.set(
      `owner_block_personal_no_${id}`,
      formatPersonalNumber(owner.personalNumber, market)
    );

  owner.dateOfBirth &&
    formData.set(
      `owner_block_date_of_birth_${id}`,
      moment(owner.dateOfBirth).format('YYYY-MM-DD')
    );

  owner.yearOfBirth &&
    formData.set(
      `owner_block_date_of_birth_${id}`,
      moment(owner.yearOfBirth).format('YYYY-MM-DD')
    );

  // TODO: once BO accepts undefined percent for ABOs, change this to only set prop if UBO
  formData.set(
    `owner_block_percent_${id}`,
    owner.isAlternativeBeneficialOwner || nonOneOwn25 || !owner.percentage
      ? '0'
      : owner.percentage
  );

  setAMLData(formData, owner, 'owner_block_', `_${id}`);
};

const setSigner = (
  signer: ISigner,
  formData: URLSearchParams,
  id: string | number
) => {
  formData.append('signer_block', id.toString());
  formData.set(`signer_email_${id}`, signer.email || '');
  formData.set(`signer_phone_${id}`, signer.phone || '');
  setAMLData(formData, signer, 'signer_', `_${id}`);
};

export const buildPostOfferFormData = (
  data: Omit<ILoanOfferForm, 'payoutAmount'> & { amount: number },
  offerInterface: string
) => {
  const market = parseMarket(window.__QRED_MARKET__);
  const formData = new URLSearchParams();
  formData.set('amount', String(data.amount));
  formData.set('client_id', String(data.clientId));
  formData.set('term', String(data.loanPeriod));
  market &&
    formData.set(
      'client_account',
      formatBankAccountNumber(data.accountNumber, market)
    );
  formData.set('invoice_email', data.invoiceEmail);
  formData.set('loan_purpose', data.purposeOfLoan);
  formData.set('loan_purpose_manual', data.loanPurposeManual);
  formData.set('account', JSON.stringify(data.account));
  formData.set(
    'open_banking_account',
    data.openBankingAccount ? JSON.stringify(data.openBankingAccount) : 'null'
  );
  formData.set(
    'owner_type',
    getOwnerType(data.applicant.percentage, data.owners)
  );
  formData.set('need_paper_invoice', data.regularMailForInvoices ? '1' : '0');
  formData.set('is_terms_approved', data.offerIsAgreed ? '1' : '0');
  formData.set(
    'is_personal_guarantee_approved',
    data.personalLiabilityIsAgreed ? '1' : '0'
  );
  formData.set('is_document_approved', data.offerIsAgreed ? '1' : '0');
  formData.set('interface', offerInterface);
  formData.set(
    'is_direct_debit_enabled',
    data.isDirectDebitEnabled ? '1' : '0'
  );

  const allSigners = [...data.signers, ...data.guarantors];
  allSigners.forEach((signer) => {
    setSigner(signer, formData, signer.signerId);
  });

  let allOwners = [...data.owners];

  const applicantInOwnersList = allOwners.find((owner) =>
    personsAreTheSame(owner, data.applicant)
  );

  if (applicantInOwnersList) {
    // make sure that we put the applicant first in the list
    allOwners = allOwners.filter(
      (owner) => applicantInOwnersList.id !== owner.id
    );
    allOwners = [applicantInOwnersList, ...allOwners];
  } else {
    allOwners = [data.applicant, ...allOwners];
  }

  allOwners.forEach((owner, index) => {
    const indexString = (index + 1).toString().padStart(2, 'O');
    setOwner(owner, formData, indexString);
  });

  return formData;
};

export const showRadioInputDependantOnQuestionId = (questionId: number) => {
  const questionIdHasRadioButtonAnswers = [103, 107];
  const shouldShowRadioInputOption = questionIdHasRadioButtonAnswers.includes(
    questionId
  );
  return shouldShowRadioInputOption;
};

export const getKYCQuestionTranslationDependantOnQuestionId = (
  questionId: number
) => {
  const questionMapping: { [key: number]: string } = {
    100: 'OnboardingOffer.KYCQuestion100',
    101: 'OnboardingOffer.KYCQuestion101',
    102: 'OnboardingOffer.KYCQuestion102',
    103: 'OnboardingOffer.KYCQuestion103',
    104: 'OnboardingOffer.KYCQuestion104',
    105: 'OnboardingOffer.KYCQuestion105',
    106: 'OnboardingOffer.KYCQuestion106',
    107: 'OnboardingOffer.KYCQuestion107',
  };

  return questionMapping[questionId];
};

const validateAMLFields = ({
  countryOfResidence,
  countryOfTaxResidence,
  isPEP,
}: IAMLData) => {
  let isValid = false;
  if (countryOfResidence && countryOfTaxResidence && isPEP !== undefined) {
    isValid = true;
  }
  return isValid;
};

export const validateOfferApplicant = (
  applicant: ICardOfferApplicant | IBeneficialOwner
) => validateAMLFields(applicant);

export const validateOfferSigner = (signer: ISigner) => {
  const { email, phone } = signer;
  const isValid = email && phone && validateAMLFields(signer);

  return isValid;
};

export const validateOfferGuarantor = (guarantor: ISigner) => {
  const { email, phone } = guarantor;
  let isValid = false;
  if (email && phone) {
    isValid = true;
  }
  return isValid;
};

export const validateFetchedBeneficialOwner = (
  beneficialOwner: IBeneficialOwner
) => {
  const market = parseMarket(window.__QRED_MARKET__);

  const {
    fullName,
    personalNumber,
    percentage,
    countryOfTaxResidence,
    isPEP,
    dateOfBirth,
    yearOfBirth,
    address,
  } = beneficialOwner;
  let isValid = false;

  switch (market) {
    case CountryCode.SE:
      if (
        fullName &&
        personalNumber &&
        percentage &&
        validateAMLFields(beneficialOwner)
      ) {
        isValid = true;
      }
      break;
    case CountryCode.FI:
      if (
        fullName &&
        percentage &&
        dateOfBirth &&
        validateAMLFields(beneficialOwner)
      ) {
        isValid = true;
      }
      break;
    case CountryCode.DK:
      if (fullName && percentage && validateAMLFields(beneficialOwner)) {
        isValid = true;
      }
      break;
    case CountryCode.NO:
      if (
        fullName &&
        percentage &&
        yearOfBirth &&
        countryOfTaxResidence &&
        isPEP !== undefined
      ) {
        isValid = true;
      }
      break;
    case CountryCode.DE:
      if (
        fullName &&
        percentage &&
        dateOfBirth &&
        address &&
        validateAMLFields(beneficialOwner)
      ) {
        isValid = true;
      }
      break;
    default:
      break;
  }
  return isValid;
};

export const getFullName = (firstName: string, secondName: string) => {
  const fullName = `${firstName} ${secondName}`.trim();
  return fullName;
};

/**
 * Check if the AML data is different between two persons.
 */
const AMLDataIsDifferent = (person1: IAMLData, person2: IAMLData) => {
  const isDifferent =
    person1.countryOfResidence !== person2.countryOfResidence ||
    person1.countryOfTaxResidence !== person2.countryOfTaxResidence ||
    person1.isPEP !== person2.isPEP;

  return isDifferent;
};

const AML_FIELDS: ReadonlyArray<keyof IAMLData> = [
  'countryOfResidence',
  'countryOfTaxResidence',
  'isPEP',
] as const;

/**
 * Check if a partial person update contains any AML field.
 */
const hasAMLChanges = (changes: Partial<IAMLData>): boolean =>
  AML_FIELDS.some((field) => field in changes);

/**
 * An alternative beneficial owner card has no ownership share of its own. When the applicant is
 * that owner, the card's empty percentage must never overwrite the percentage the applicant entered.
 */
const ownerHasOwnershipShare = (owner: IBeneficialOwner): boolean =>
  !owner.isAlternativeBeneficialOwner && !!owner.percentage;

/**
 * Extracts the AML data from a person.
 */
const extractAMLData = ({
  countryOfTaxResidence,
  countryOfResidence,
  isPEP,
}: IAMLData): IAMLData => ({
  countryOfTaxResidence,
  countryOfResidence,
  isPEP,
});

/**
 * Whenever an owner is updated, we need to check if the same person is in the
 * list of signers and/or the applicant and update it there as well.
 */
const handleBeneficialOwnerChange = (
  beneficialOwner: IBeneficialOwner,
  state: ICardOfferState | ILoanOfferState,
  dispatch: AppDispatch,
  actions: TLoanOfferSliceActions | TCardOfferSliceActions
) => {
  const matchingSigner = state.form.signers.find((s) =>
    personsAreTheSame(s, beneficialOwner)
  );
  if (matchingSigner && AMLDataIsDifferent(matchingSigner, beneficialOwner)) {
    dispatch(
      actions.updateSigner({
        ...matchingSigner,
        ...extractAMLData(beneficialOwner),
      })
    );
  }

  const matchingApplicant =
    personsAreTheSame(state.form.applicant, beneficialOwner) &&
    state.form.applicant;
  const shouldSyncPercentage = ownerHasOwnershipShare(beneficialOwner);
  if (
    matchingApplicant &&
    (AMLDataIsDifferent(matchingApplicant, beneficialOwner) ||
      (shouldSyncPercentage &&
        matchingApplicant.percentage !== beneficialOwner.percentage) ||
      JSON.stringify(matchingApplicant.address) !==
        JSON.stringify(beneficialOwner.address))
  ) {
    dispatch(
      actions.updateApplicant({
        ...extractAMLData(beneficialOwner),
        ...(shouldSyncPercentage && { percentage: beneficialOwner.percentage }),
        address: beneficialOwner.address,
        dateOfBirth:
          beneficialOwner.dateOfBirth ?? state.form.applicant.dateOfBirth,
      })
    );
  }
};

const handleBeneficialOwnerRemoved = (
  beneficialOwner: IBeneficialOwner,
  state: ICardOfferState | ILoanOfferState,
  dispatch: AppDispatch,
  actions: TLoanOfferSliceActions | TCardOfferSliceActions
) => {
  const matchingApplicant =
    personsAreTheSame(state.form.applicant, beneficialOwner) &&
    state.form.applicant;
  if (matchingApplicant) {
    dispatch(
      actions.updateApplicant({
        percentage: '0',
      })
    );
  }
};

/**
 * Whenever a signer is updated, we need to check if the same person is in the list of owners,
 * and then update it there as well.
 */
const handleSignerChange = (
  signer: ISigner,
  state: ICardOfferState | ILoanOfferState,
  dispatch: AppDispatch,
  actions: TLoanOfferSliceActions | TCardOfferSliceActions
) => {
  const matchingOwner = state.form.owners.find((o) =>
    personsAreTheSame(o, signer)
  );
  if (matchingOwner && AMLDataIsDifferent(matchingOwner, signer)) {
    dispatch(
      actions.updateAdditionalOwner({
        ...matchingOwner,
        ...extractAMLData(signer),
      })
    );
  }
};

/**
 * Whenever the applicant is updated, we need to check if the same person is in the list of owners,
 * and then update it there as well.
 *
 * The action payload is a partial update (e.g. only `percentage`), so the AML data is read from the
 * merged applicant in state, and only synced when the update actually touched an AML field. Reading
 * it from a percentage-only payload wiped the owner card's AML answers.
 *
 * @param applicantChanges the `updateApplicant` payload, only the fields that changed
 * @param state the offer state after the update, holding the merged applicant
 */
const handleApplicantChange = (
  applicantChanges: Partial<ILoanOfferApplicant | ICardOfferApplicant>,
  state: ICardOfferState | ILoanOfferState,
  dispatch: AppDispatch,
  actions: TLoanOfferSliceActions | TCardOfferSliceActions
) => {
  if (!hasAMLChanges(applicantChanges)) {
    return;
  }

  const fullApplicantObject = state.form.applicant;
  const matchingOwner = state.form.owners.find((o) =>
    personsAreTheSame(o, fullApplicantObject)
  );
  if (matchingOwner && AMLDataIsDifferent(matchingOwner, fullApplicantObject)) {
    dispatch(
      actions.updateAdditionalOwner({
        ...matchingOwner,
        ...extractAMLData(fullApplicantObject),
      })
    );
  }
};

/**
 * Whenever the list of owners is updated, we need to check each owner against the applicant and signers
 * and update them if they match.
 */
const handleBeneficialOwnersListChange = (
  beneficialOwners: IBeneficialOwner[],
  state: ICardOfferState | ILoanOfferState,
  dispatch: AppDispatch,
  actions: TLoanOfferSliceActions | TCardOfferSliceActions
) => {
  const ownerMatchingApplicant = beneficialOwners.find((o) =>
    personsAreTheSame(o, state.form.applicant)
  );
  const shouldSyncPercentage =
    !!ownerMatchingApplicant && ownerHasOwnershipShare(ownerMatchingApplicant);
  if (
    ownerMatchingApplicant &&
    (AMLDataIsDifferent(ownerMatchingApplicant, state.form.applicant) ||
      (shouldSyncPercentage &&
        ownerMatchingApplicant.percentage !== state.form.applicant.percentage))
  ) {
    const { percentage, ...ownerWithoutPercentage } = ownerMatchingApplicant;
    dispatch(
      actions.updateApplicant({
        ...ownerWithoutPercentage,
        ...(shouldSyncPercentage && { percentage }),
        dateOfBirth:
          ownerMatchingApplicant.dateOfBirth ??
          state.form.applicant.dateOfBirth,
      })
    );
  }

  const signers = state.form.signers ?? [];
  beneficialOwners.forEach((o) => {
    const matchingSigner = signers.find((s) => personsAreTheSame(s, o));
    if (matchingSigner && AMLDataIsDifferent(matchingSigner, o)) {
      dispatch(
        actions.updateSigner({
          ...matchingSigner,
          ...extractAMLData(o),
        })
      );
    }
  });
};

/**
 * Syncs the AML/KYC data between the applicant and the owners/signers for both card and loan offer flows.
 * @param action { type, payload } where type can be 'loanOffer/*' or 'cardOffer/*' where * can be any of the following:
 * - addAdditionalOwner
 * - updateAdditionalOwner
 * - updateSigner
 * - updateApplicant
 * - setBeneficialOwners
 * - removeAdditionalOwnerById
 * and payload can be any of the following:
 * - IBeneficialOwner
 * - ISigner
 * - ILoanOfferApplicant
 * - ICardOfferApplicant
 * - IBeneficialOwner[]
 * - string
 */
export const syncOfferPersons = (
  action: AnyAction,
  state: ICardOfferState | ILoanOfferState,
  originalState: ICardOfferState | ILoanOfferState,
  dispatch: AppDispatch,
  actions: TLoanOfferSliceActions | TCardOfferSliceActions
) => {
  // Pick out the last part of the action type, e.g. 'addAdditionalOwner' or 'updateAdditionalOwner'
  const [caseReducer] = action.type.split('/').reverse();

  switch (caseReducer) {
    case 'addAdditionalOwner':
    case 'updateAdditionalOwner': {
      const owner: IBeneficialOwner = action.payload;
      handleBeneficialOwnerChange(owner, state, dispatch, actions);
      break;
    }
    case 'updateSigner': {
      const signer: ISigner = action.payload;
      // We only care about signer with type Signer, not Guarantor. Guarantors and owners are not sharing any data.
      if (signer.type === SignerType.Signer) {
        handleSignerChange(signer, state, dispatch, actions);
      }
      break;
    }
    case 'updateApplicant': {
      const applicantChanges: Partial<
        ILoanOfferApplicant | ICardOfferApplicant
      > = action.payload;
      handleApplicantChange(applicantChanges, state, dispatch, actions);
      break;
    }
    case 'setBeneficialOwners': {
      const beneficialOwners: IBeneficialOwner[] = action.payload;
      if (beneficialOwners.length) {
        handleBeneficialOwnersListChange(
          beneficialOwners,
          state,
          dispatch,
          actions
        );
      }
      break;
    }
    case 'removeAdditionalOwnerById': {
      const removedOwnerId: string = action.payload;
      const removedOwner = originalState.form.owners.find(
        (owner) => owner.id === removedOwnerId
      );
      if (removedOwner) {
        handleBeneficialOwnerRemoved(removedOwner, state, dispatch, actions);
      }
      break;
    }
    default:
      break;
  }
};

export const getBankAccountNumber = (
  account: IKlarnaAccount,
  market: CountryCode
): string => {
  if (market === CountryCode.SE) {
    return `${account.bank_code || ''}${account.account_number || ''}`;
  }
  if (account.iban) {
    return account.iban;
  }
  return '';
};

export const getOpenBankingAccountNumber = (
  account: IOpenBankingAccount
): string => {
  switch (account.market) {
    case CountryCode.SE:
    case CountryCode.NO:
    case CountryCode.DK:
      return account.bban;
    case CountryCode.FI:
    case CountryCode.BE:
    case CountryCode.NL:
    case CountryCode.DE:
      return account.iban;
  }
};

export const applicationCanHaveGuarantors = (
  market: CountryCode,
  companyTypeCategory: CompanyTypeCategory | undefined,
  guarantors: ILoanApplicationGuarantor[],
  guarantorsApiStatus: ApiStatus
) => {
  const isSoleTraderNotInBE =
    companyTypeCategory === CompanyTypeCategory.SOLE_TRADER &&
    market !== CountryCode.BE;

  const hasNoGuarantorsInDE =
    market === CountryCode.DE &&
    !guarantors.length &&
    guarantorsApiStatus === ApiStatus.Completed;

  return !isSoleTraderNotInBE && !hasNoGuarantorsInDE;
};
